import { useEffect, useRef, useState } from 'react';

/**
 * FPS overlay — enabled by adding ?fps=1 to the URL.
 * Uses requestAnimationFrame to measure real browser frame rate.
 * Updates the display every 500 ms to stay readable.
 *
 * Colour coding:
 *   green  ≥ 55 fps  — smooth
 *   yellow ≥ 30 fps  — acceptable
 *   red    < 30 fps  — dropping
 */
export function FpsCounter() {
  const [fps, setFps] = useState<number>(0);
  const [low, setLow] = useState<number>(Infinity); // lowest reading in the current session
  const frameCountRef = useRef(0);
  const lastTimeRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const lowRef = useRef(Infinity);

  useEffect(() => {
    lastTimeRef.current = performance.now();
    const tick = () => {
      frameCountRef.current += 1;
      const now = performance.now();
      const elapsed = now - lastTimeRef.current;

      if (elapsed >= 500) {
        const current = Math.round((frameCountRef.current / elapsed) * 1000);
        if (current < lowRef.current) {
          lowRef.current = current;
          setLow(current);
        }
        setFps(current);
        frameCountRef.current = 0;
        lastTimeRef.current = now;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const getFpsColor = (value: number) => {
    if (value >= 55) return '#4ade80';
    if (value >= 30) return '#facc15';
    return '#f87171';
  };

  const fpsColor = getFpsColor(fps);
  const lowColor = getFpsColor(low);

  return (
    <div
      style={{
        position: 'fixed',
        top: 8,
        left: 8,
        zIndex: 99999,
        padding: '5px 9px',
        borderRadius: 6,
        background: 'rgba(0,0,0,0.75)',
        border: '1px solid rgba(255,255,255,0.12)',
        fontFamily: 'monospace',
        fontSize: 12,
        lineHeight: 1.6,
        pointerEvents: 'none',
        userSelect: 'none',
        backdropFilter: 'blur(4px)',
        minWidth: 80,
      }}
    >
      <div style={{ color: fpsColor, fontWeight: 700 }}>{String(fps).padStart(3, ' ')} FPS</div>
      <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10 }}>
        {'low '}
        <span style={{ color: lowColor }}>
          {low === Infinity ? '—' : String(low).padStart(3, ' ')}
        </span>
      </div>
    </div>
  );
}
